import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Field,
  Input,
  Select,
  StatTile,
  humanizeStatus,
} from '@biovet/ui';
import { auth } from '../lib/actor.js';
import { apiSend, useResource, type AdminWriteResult } from '../lib/api.js';
import type { RecordSet } from '../lib/design-data.js';
import { RecordGrid } from './Records.js';
import { Messages, useAction } from './document.js';

/**
 * The two counterparties a brokered deal is struck between.
 *
 * They were maintained nowhere. `parties` has carried `is_broker_seller` and
 * `is_broker_buyer` since `0007`, the seed inserted seven of them, and the only
 * way to add an eighth was SQL — the party register in Sales & Purchase
 * deliberately excludes both flags, on the grounds that brokerage counterparties
 * "are edited on the deal screens", which was true of no screen. This is the
 * register those two sentences were promising.
 *
 * **The two sides are not symmetric, and the screen says so rather than hiding
 * it.** Party A owes us commission — that is the single entry Part 1 makes in
 * the ledger — so saving one opens their own level-5 account under
 * `1-1-3-2 Commission Receivable — Suppliers` and the register prints the code
 * beside them. Party B pays the supplier direct, none of that money passes
 * through our books, and `0015_invariants.sql` refuses a voucher sourced from
 * `deals` outright: so Party B gets no account, and the form drops the fields
 * that only mean something to one — a commission basis, a rate, a currency to
 * bill in.
 *
 * The Party A and Party B pickers on Deal Entry read this register and nothing
 * else. They used to fall back to every supplier and every customer in the book,
 * which offered a feed supplier we buy from as a party who owes us commission.
 */

const R = 'right' as const;

interface DealPartyRow {
  id: string;
  code: string;
  name: string;
  legalName: string | null;
  isSeller: boolean;
  isBuyer: boolean;
  origin: string;
  ntn: string | null;
  taxId: string | null;
  contactPerson: string | null;
  phone: string | null;
  email: string | null;
  addressLine1: string | null;
  city: string | null;
  countryCode: string | null;
  currency: string;
  commissionBasis: string | null;
  commissionRate: string | null;
  commissionCurrency: string | null;
  isActive: boolean;
  /** The chart row a Party A owns. Null for Party B, by design. */
  commissionAccount: string | null;
  commissionAccountName: string | null;
  dealCount: number;
  ledgerLines: number;
  offerCount: number;
}

interface DealPartyResponse {
  parties: DealPartyRow[];
  currencies: Array<{ code: string; name: string }>;
  origins: string[];
  commissionBases: string[];
  /** Why the sub-ledger is not being opened, when it is not. */
  subLedgerBlocked: string | null;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

interface Draft {
  original: string | null;
  code: string;
  name: string;
  side: 'A' | 'B';
  legalName: string;
  origin: string;
  currency: string;
  ntn: string;
  taxId: string;
  contactPerson: string;
  phone: string;
  email: string;
  addressLine1: string;
  city: string;
  commissionBasis: string;
  commissionRate: string;
  commissionCurrency: string;
  isActive: boolean;
}

function blankDraft(side: 'A' | 'B', baseCurrency: string): Draft {
  return {
    original: null,
    code: '',
    name: '',
    side,
    legalName: '',
    origin: 'LOCAL',
    currency: baseCurrency,
    ntn: '',
    taxId: '',
    contactPerson: '',
    phone: '',
    email: '',
    addressLine1: '',
    city: '',
    commissionBasis: 'PCT_OF_DEAL_VALUE',
    commissionRate: '',
    commissionCurrency: baseCurrency,
    isActive: true,
  };
}

function draftOf(row: DealPartyRow): Draft {
  return {
    original: row.code,
    code: row.code,
    name: row.name,
    side: row.isSeller ? 'A' : 'B',
    legalName: row.legalName ?? '',
    origin: row.origin,
    currency: row.currency,
    ntn: row.ntn ?? '',
    taxId: row.taxId ?? '',
    contactPerson: row.contactPerson ?? '',
    phone: row.phone ?? '',
    email: row.email ?? '',
    addressLine1: row.addressLine1 ?? '',
    city: row.city ?? '',
    commissionBasis: row.commissionBasis ?? 'PCT_OF_DEAL_VALUE',
    commissionRate: row.commissionRate ?? '',
    commissionCurrency: row.commissionCurrency ?? 'PKR',
    isActive: row.isActive,
  };
}

/**
 * The draft as the API wants it.
 *
 * The commission trio is sent only for a Party A. Sending it blank for a Party B
 * would be sending three fields the server then has to decide to ignore, and a
 * field that is accepted and discarded is a field somebody will one day fill in
 * and wonder about.
 */
function payloadOf(draft: Draft): Record<string, unknown> {
  const base: Record<string, unknown> = {
    code: draft.code.trim().toUpperCase(),
    name: draft.name.trim(),
    side: draft.side,
    legalName: draft.legalName.trim() || null,
    origin: draft.origin,
    currency: draft.currency,
    ntn: draft.ntn.trim() || null,
    taxId: draft.taxId.trim() || null,
    contactPerson: draft.contactPerson.trim() || null,
    phone: draft.phone.trim() || null,
    email: draft.email.trim() || null,
    addressLine1: draft.addressLine1.trim() || null,
    city: draft.city.trim() || null,
    isActive: draft.isActive,
  };
  if (draft.side === 'A') {
    base.commissionBasis = draft.commissionBasis || null;
    base.commissionRate = draft.commissionRate.trim() || null;
    base.commissionCurrency = draft.commissionCurrency;
  }
  return base;
}

const FILTERS: Array<{ label: string; test: (p: DealPartyRow) => boolean }> = [
  { label: 'All', test: () => true },
  { label: 'Party A — suppliers', test: (p) => p.isSeller },
  { label: 'Party B — buyers', test: (p) => p.isBuyer },
  { label: 'No chart account', test: (p) => p.isSeller && !p.commissionAccount },
];

function describeCommission(row: DealPartyRow): string {
  if (!row.isSeller) return '—';
  const rate = row.commissionRate ? Number(row.commissionRate).toString() : null;
  if (row.commissionBasis === 'PCT_OF_DEAL_VALUE') return rate ? `${rate}% of deal value` : '—';
  if (row.commissionBasis === 'PER_UNIT') {
    return rate ? `${row.commissionCurrency} ${rate} per unit` : '—';
  }
  if (row.commissionBasis === 'FLAT_AMOUNT') return 'Flat amount';
  return '—';
}

export function DealPartiesScreen() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const res = useResource<DealPartyResponse>('/deal-parties', {
    include: includeInactive ? 'inactive' : undefined,
  });
  const { busy, notice, problem, run } = useAction(res.reload);

  const [chip, setChip] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);

  const all = useMemo(() => res.data?.parties ?? [], [res.data]);
  const rows = useMemo(() => all.filter(FILTERS[chip]?.test ?? (() => true)), [all, chip]);
  const chosen = selected !== null ? rows[selected] : undefined;

  const currencies = res.data?.currencies ?? [];
  const baseCurrency = currencies[0]?.code ?? 'PKR';
  const canEdit = res.data?.canEdit ?? false;

  const set: RecordSet = {
    crumb: 'Brokerage / Deal Parties',
    title: 'Deal parties',
    subtitle: 'The sellers and buyers you broker deals between.',
    chips: FILTERS.map((f) => f.label),
    cols: [
      { label: 'Code', width: '120px', mono: true },
      { label: 'Name' },
      { label: 'Side', width: '86px' },
      { label: 'Contact' },
      { label: 'Commission', width: '170px' },
      { label: 'Chart account', width: '190px', mono: true },
      { label: 'Deals', align: R, mono: true, width: '76px' },
      { label: 'Status', align: R, width: '104px', badge: true },
    ],
    rows: rows.map((p) => [
      p.code,
      p.legalName && p.legalName !== p.name ? `${p.name} · ${p.legalName}` : p.name,
      p.isSeller ? 'Party A' : 'Party B',
      [p.contactPerson, p.phone].filter(Boolean).join(' · ') || '—',
      describeCommission(p),
      p.commissionAccount ? `${p.commissionAccount} ${p.commissionAccountName ?? ''}`.trim() : '—',
      String(p.dealCount),
      p.isActive ? 'ACTIVE' : 'INACTIVE',
    ]),
  };

  const save = async () => {
    if (!draft) return;
    const body = payloadOf(draft);
    const ok = await run(() =>
      draft.original
        ? apiSend<AdminWriteResult>(
            'PATCH',
            `/deal-parties/${encodeURIComponent(draft.original)}`,
            body,
            auth(),
          )
        : apiSend<AdminWriteResult>('POST', '/deal-parties', body, auth()),
    );
    if (ok) setDraft(null);
  };

  const partyA = all.filter((p) => p.isSeller);

  return (
    <RecordGrid
      set={set}
      loading={res.loading}
      error={res.error}
      chip={chip}
      onChipChange={(i) => {
        setChip(i);
        setSelected(null);
      }}
      onRowSelect={setSelected}
      selectedRow={selected}
      hideGrid={draft !== null}
      emptyTitle="No deal parties yet"
      actions={
        <>
          <Button
            size="sm"
            variant={includeInactive ? 'secondary' : 'ghost'}
            onClick={() => setIncludeInactive((v) => !v)}
          >
            {includeInactive ? 'Hide inactive' : 'Show inactive'}
          </Button>
          <Button
            size="sm"
            disabled={!res.data?.canCreate}
            onClick={() => {
              setSelected(null);
              setDraft(blankDraft('B', baseCurrency));
            }}
          >
            New Party B
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={!res.data?.canCreate}
            onClick={() => {
              setSelected(null);
              setDraft(blankDraft('A', baseCurrency));
            }}
          >
            New Party A
          </Button>
        </>
      }
    >
      <Messages notice={notice} problem={problem} />

      {/*
        Why a Party A has no account code beside them, when they have none.

        The break-out below 1-1-3-2 waits while a commission invoice is still
        outstanding against the control account: a control account with children
        takes no line at all, so opening the first child would leave that invoice
        with no instrument that can clear it. Saying so here beats leaving
        somebody to wonder why the column is empty — and it resolves itself, so
        the sentence names what to do rather than reporting a fault.
      */}
      {res.data?.subLedgerBlocked && (
        <div className="pb-3.5" data-noprint="1">
          <Callout tone="warn" title="Party A accounts are waiting on an outstanding invoice">
            {res.data.subLedgerBlocked} Until then a commission invoice posts to the control
            account, exactly as it did before — nothing is lost, and the accounts open by themselves
            once the balance is cleared.
          </Callout>
        </div>
      )}

      {!draft && (
        <div className="grid grid-cols-2 gap-3.5 pb-3.5 lg:grid-cols-4" data-noprint="1">
          <Card>
            <StatTile
              label="Party A — suppliers"
              value={String(partyA.length)}
              hint="invoiced commission"
            />
          </Card>
          <Card>
            <StatTile
              label="Party B — buyers"
              value={String(all.filter((p) => p.isBuyer).length)}
              hint="no ledger entry"
            />
          </Card>
          <Card>
            <StatTile
              label="With a chart account"
              value={String(partyA.filter((p) => p.commissionAccount).length)}
              hint="under 1-1-3-2"
            />
          </Card>
          <Card>
            <StatTile
              label="On a deal"
              value={String(all.filter((p) => p.dealCount > 0).length)}
              hint="cannot be deleted"
            />
          </Card>
        </div>
      )}

      {chosen && !draft && (
        <Card flush data-noprint="1" className="mb-3.5">
          <CardHeader
            title={`${chosen.code} — ${chosen.name}`}
            subtitle={
              chosen.isSeller
                ? chosen.commissionAccount
                  ? `Party A · commission posts to ${chosen.commissionAccount} ${chosen.commissionAccountName ?? ''}`
                  : 'Party A · commission posts to the control account for now'
                : 'Party B · no chart account, and none is needed'
            }
            actions={
              <div className="flex gap-2">
                <Button size="sm" disabled={!canEdit} onClick={() => setDraft(draftOf(chosen))}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  loading={busy}
                  disabled={!res.data?.canDelete}
                  onClick={async () => {
                    const ok = await run(() =>
                      apiSend<AdminWriteResult>(
                        'DELETE',
                        `/deal-parties/${encodeURIComponent(chosen.code)}`,
                        undefined,
                        auth(),
                      ),
                    );
                    if (ok) setSelected(null);
                  }}
                >
                  Delete
                </Button>
              </div>
            }
          />
          <CardBody className="flex flex-wrap gap-x-8 gap-y-2 text-[12.5px]">
            <Fact label="Origin" value={humanizeStatus(chosen.origin)} />
            <Fact label="Currency" value={chosen.currency} />
            {chosen.isSeller && <Fact label="Commission" value={describeCommission(chosen)} />}
            <Fact label="Contact" value={chosen.contactPerson ?? '—'} />
            <Fact label="Phone" value={chosen.phone ?? '—'} />
            <Fact label="Email" value={chosen.email ?? '—'} />
            <Fact
              label="Address"
              value={[chosen.addressLine1, chosen.city].filter(Boolean).join(', ') || '—'}
            />
            <Fact label="Deals" value={String(chosen.dealCount)} />
            {chosen.isSeller && (
              <Fact label="Offers on the map" value={String(chosen.offerCount)} />
            )}
            <span className="flex items-center gap-2">
              <Badge status={chosen.isActive ? 'ACTIVE' : 'INACTIVE'}>
                {chosen.isActive ? 'active' : 'inactive'}
              </Badge>
            </span>
          </CardBody>
        </Card>
      )}

      {draft && (
        <Card flush data-noprint="1" className="mb-3.5">
          <CardHeader
            title={
              draft.original
                ? `Edit ${draft.original}`
                : draft.side === 'A'
                  ? 'New Party A — supplier, pays us commission'
                  : 'New Party B — buyer, pays Party A direct'
            }
            subtitle={
              draft.side === 'A'
                ? 'Saving opens their own account under 1-1-3-2 Commission Receivable — Suppliers'
                : 'No chart account: none of this party’s money passes through our books'
            }
            actions={
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" onClick={() => setDraft(null)} disabled={busy}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  loading={busy}
                  disabled={draft.code.trim() === '' || draft.name.trim() === ''}
                  onClick={save}
                >
                  {draft.original ? 'Save' : `Create Party ${draft.side}`}
                </Button>
              </div>
            }
          />
          <CardBody className="grid grid-cols-2 gap-2.5 md:grid-cols-4">
            <Field label="Side" required>
              <Select
                size="sm"
                value={draft.side}
                onChange={(e) => setDraft({ ...draft, side: e.target.value as 'A' | 'B' })}
              >
                <option value="A">Party A — supplier</option>
                <option value="B">Party B — buyer</option>
              </Select>
            </Field>
            <Field
              label="Code"
              required
              hint={draft.original ? 'Frozen once a deal names them' : 'PTA-0004, PTB-0003'}
            >
              <Input
                size="sm"
                value={draft.code}
                disabled={Boolean(draft.original)}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="Name" required className="md:col-span-2">
              <Input
                size="sm"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Andes Protein SA"
              />
            </Field>

            <Field label="Legal name" className="md:col-span-2">
              <Input
                size="sm"
                value={draft.legalName}
                onChange={(e) => setDraft({ ...draft, legalName: e.target.value })}
              />
            </Field>
            <Field label="Origin">
              <Select
                size="sm"
                value={draft.origin}
                onChange={(e) => setDraft({ ...draft, origin: e.target.value })}
              >
                {(res.data?.origins ?? ['LOCAL', 'FOREIGN']).map((o) => (
                  <option key={o} value={o}>
                    {humanizeStatus(o)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Currency">
              <Select
                size="sm"
                value={draft.currency}
                onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
              >
                {currencies.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code}
                  </option>
                ))}
              </Select>
            </Field>

            {/*
              The commission trio renders for Party A alone. A default basis and
              rate on a buyer would be three fields describing money we never
              bill them — and the server drops them for the same reason.
            */}
            {draft.side === 'A' && (
              <>
                <Field label="Default commission basis" className="md:col-span-2">
                  <Select
                    size="sm"
                    value={draft.commissionBasis}
                    onChange={(e) => setDraft({ ...draft, commissionBasis: e.target.value })}
                  >
                    <option value="PCT_OF_DEAL_VALUE">Percentage of deal value</option>
                    <option value="PER_UNIT">Per unit</option>
                    <option value="FLAT_AMOUNT">Flat amount</option>
                  </Select>
                </Field>
                <Field
                  label={draft.commissionBasis === 'PCT_OF_DEAL_VALUE' ? 'Percentage' : 'Per unit'}
                  hint="A starting point — the deal decides"
                >
                  <Input
                    size="sm"
                    numeric
                    value={draft.commissionRate}
                    onChange={(e) => setDraft({ ...draft, commissionRate: e.target.value })}
                    placeholder="1.25"
                  />
                </Field>
                <Field label="Billed in">
                  <Select
                    size="sm"
                    value={draft.commissionCurrency}
                    onChange={(e) => setDraft({ ...draft, commissionCurrency: e.target.value })}
                  >
                    {currencies.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code}
                      </option>
                    ))}
                  </Select>
                </Field>
              </>
            )}

            <Field label="Contact person">
              <Input
                size="sm"
                value={draft.contactPerson}
                onChange={(e) => setDraft({ ...draft, contactPerson: e.target.value })}
              />
            </Field>
            <Field label="Phone">
              <Input
                size="sm"
                value={draft.phone}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </Field>
            <Field label="Email" className="md:col-span-2">
              <Input
                size="sm"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              />
            </Field>

            <Field label="Address" className="md:col-span-2">
              <Input
                size="sm"
                value={draft.addressLine1}
                onChange={(e) => setDraft({ ...draft, addressLine1: e.target.value })}
              />
            </Field>
            <Field label="City">
              <Input
                size="sm"
                value={draft.city}
                onChange={(e) => setDraft({ ...draft, city: e.target.value })}
              />
            </Field>
            <Field label={draft.origin === 'FOREIGN' ? 'Tax ID' : 'NTN'}>
              <Input
                size="sm"
                value={draft.origin === 'FOREIGN' ? draft.taxId : draft.ntn}
                onChange={(e) =>
                  setDraft(
                    draft.origin === 'FOREIGN'
                      ? { ...draft, taxId: e.target.value }
                      : { ...draft, ntn: e.target.value },
                  )
                }
              />
            </Field>

            <div className="md:col-span-4">
              <Checkbox
                checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
                label="Active — offered in the Deal Entry pickers"
              />
            </div>
          </CardBody>
        </Card>
      )}
    </RecordGrid>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <span className="flex flex-col">
      <span className="bb-eyebrow">{label}</span>
      <span className="text-[var(--ink-2)]">{value}</span>
    </span>
  );
}
